import { useEffect, useRef, useState } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import { authReducer } from '@bitstopco/bitstop-theme';
import { LoadingButton } from '@mui/lab';
import { Box, Divider, Stack, Typography } from '@mui/material';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import { getPostLoginPath, hasAuthorizationCode, parseAuth0Error } from '@/helpers/auth0';
import { clearAuth0Session, customAuthActions, storeAuth0Session } from '@/helpers/auth0Session';
import { useAppDispatch } from '@/store';

const LoginV2 = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const {
    error,
    isLoading,
    isAuthenticated,
    loginWithRedirect,
    getAccessTokenSilently,
    getIdTokenClaims,
  } = useAuth0();

  const [isReturningFromAuth0] = useState(hasAuthorizationCode());
  const [isBusy, setIsBusy] = useState(isReturningFromAuth0);
  const hasPromotedSession = useRef(false);

  const promoteSession = async () => {
    try {
      const accessToken = await getAccessTokenSilently();
      const idTokenClaims = await getIdTokenClaims();

      storeAuth0Session(accessToken, idTokenClaims?.__raw);

      await dispatch(authReducer.thunks.userInfo({ action: customAuthActions.userInfo })).unwrap();

      navigate(`${window.location.pathname}${window.location.search}`, { replace: true });
    } catch (exception) {
      clearAuth0Session();

      setIsBusy(false);
      toast.error(parseAuth0Error(exception));
    }
  };

  const handleLogin = async () => {
    setIsBusy(true);

    try {
      await loginWithRedirect({ appState: { returnTo: getPostLoginPath() } });
    } catch (exception) {
      setIsBusy(false);
      toast.error(parseAuth0Error(exception));
    }
  };

  useEffect(() => {
    if (!error) return;

    setIsBusy(false);
    toast.error(parseAuth0Error(error));
  }, [error]);

  useEffect(() => {
    if (!isReturningFromAuth0 || !isAuthenticated || hasPromotedSession.current) return;

    hasPromotedSession.current = true;
    promoteSession();
  }, [isReturningFromAuth0, isAuthenticated]);

  return (
    <Box data-test="auth-login-v2">
      <Typography variant="h4" mb={2} textAlign="center">
        Login
      </Typography>
      <Typography textAlign="center" mb={3} color="text.secondary">
        You will be redirected to the Auth0 Universal Login page to continue.
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <Stack gap={1} alignItems="center">
        <LoadingButton
          data-test="auth-login-v2-submit-btn"
          variant="contained"
          fullWidth
          loading={isLoading || isBusy}
          onClick={handleLogin}
        >
          Login
        </LoadingButton>
      </Stack>
    </Box>
  );
};

export default LoginV2;
